'use client';

/**
 * AskRendererSlotCard — slot 4 of the flipped answer: the artifact cards.
 *
 * The dispatched-card-slot pattern (mode8 demo_ask_renderer_slot /
 * Reception): the compositor names cards by reference (`renderer_cards`
 * per facet, or the singular `renderer_card` hint); this slot mounts
 * each named card via `resolveCard` — the unified resolver across every
 * surface registry (canvas · explorer · insights · composer · chat), so
 * a recommendation resolves its card wherever it self-registered, no
 * per-surface re-registration. Content-arm entries are excluded —
 * they're the quick-answer card's source, and mounting them here would
 * render the same text twice.
 *
 * Registry access happens at RENDER time, so the registry↔slot module
 * cycle is inert (same shape as the overlay's own legacy Mount path).
 * Each child wraps in its own CardPlacementProvider with a per-instance
 * slot_id — the collection-card discipline from klyde-cards.
 *
 * @see klyde_edge/ui_config/composition/cards/ask.py — @card declaration
 *
 * @layer presentation
 * @arch_component_type widget
 * @arch_domain copilot
 */

import { useAgentCanvasStore } from '@/lib/stores/agent-canvas-store';
import { resolveCard } from '@/components/cards/unified-card-registry';
import { CardPlacementProvider, useCardExport } from '@/lib/design/card-export';
import type { CardView } from '@/lib/api/ask-types';

const str = (v: unknown): string | null => (typeof v === 'string' && v.trim() ? v : null);

interface ArtifactRef {
  card: string;
  entity: string | null;
  // The entity's TYPE — some entity-bound cards (rfi_details) key their
  // fetch on (type, sid), so the mount must hand both. Null for cards that
  // bind on the sid alone; they ignore the prop.
  entityType: string | null;
  // BFF-shipped seed for query-driven cards (the search canvas): the
  // user's own ask, carried on the facet as `renderer_query`. Pure
  // pass-through — entity-bound cards ignore the prop.
  query: string | null;
}

export function AskRendererSlotCard() {
  const compose = useAgentCanvasStore((s) => s.compose);
  const view = compose?.view ?? null;

  // Cards by reference from the CLEAN view — the proposal GROUP (`view.cards`, e.g. the
  // whole composer) if present, else the single dispatched card (`view.card`). Each card
  // carries its own entity; deduped on (card, entity). The content-arm exclusion is gone
  // — the view already separates the quick-answer source from the artifact cards.
  const seen = new Set<string>();
  const refs: ArtifactRef[] = [];
  const group: CardView[] =
    view?.cards && view.cards.length > 0 ? view.cards : view?.card ? [view.card] : [];
  for (const c of group) {
    const card = str(c.renderer_card);
    if (!card) continue;
    const entity = str(c.entity_sid);
    const key = `${card}:${entity ?? ''}`;
    if (seen.has(key)) continue;
    seen.add(key);
    // renderer_query (the search-canvas seed) isn't carried by the clean view — it was
    // already unsatisfied against the shim, so query stays null (cards ignore it).
    refs.push({ card, entity, entityType: str(c.entity_type), query: null });
  }

  useCardExport({
    content_key: 'ask_renderer_slot',
    text: refs.map((r) => r.card).join(' · '),
    json: { cards: refs.map((r) => ({ card: r.card, entity: r.entity })) },
    status: refs.length > 0 ? 'loaded' : 'idle',
    // The mounted children declare their own query_keys; the slot itself
    // fetches nothing.
    query_key: null,
  });

  if (refs.length === 0) return null;
  return (
    <div className="flex flex-col gap-3">
      {refs.map((ref) => {
        // Resolve across ALL surface registries (canvas · explorer · insights ·
        // composer · chat), not canvas alone — a recommendation names a card;
        // the slot mounts it wherever it self-registered. So a composer card
        // (rfq_create -> composer_lines) mounts here with NO canvas re-registration.
        const Mount = resolveCard(ref.card) as
          | React.ComponentType<Record<string, unknown>>
          | undefined;
        if (!Mount) return null;
        return (
          <div
            key={`${ref.card}:${ref.entity ?? ''}`}
            // Cards self-size (their charts own definite internal boxes);
            // the overlay column scrolls.
            className="shrink-0"
          >
            <CardPlacementProvider
              value={{ surface: 'overlay', slot_id: `renderer:${ref.card}` }}
            >
              <Mount
                entityId={ref.entity ?? undefined}
                entityType={ref.entityType ?? undefined}
                initialQuery={ref.query ?? undefined}
              />
            </CardPlacementProvider>
          </div>
        );
      })}
    </div>
  );
}
